import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, TimeoutError, from, lastValueFrom, of, throwError } from 'rxjs';
import { catchError, map, skipWhile, switchMap, tap } from 'rxjs/operators';

import { CategorySubcategoryRival } from '../../models/interfaces/category';
import {
  Evaluation,
  EvaluationCategory,
  EvaluationCategoryRival,
  EvaluationProduct,
  EvaluationProductRival,
  EvaluationSubCategory,
} from '../../models/interfaces/evaluation';
import { PaginatedEvaluationsRemotes } from '../model/evaluation-remote.response';
import { EvaluationValidationError, toEvaluationPayload, validateEvaluation } from '../utils/evaluation-payload.mapper';
import { StorageEntityName } from '../model/storage.model';
import { AuthService } from './auth.service';
import { FileService } from './file.service';
import { NetworkService } from './network.service';
import { PhotoService } from './photo.service';
import { SentryService } from './sentry.service';
import { StorageService } from './storage.service';
import { GeolocationService } from './geolocation.service';
import structuredClone from '@ungap/structured-clone';

export interface SendStep {
  id: 'mapping' | 'evidences' | 'exhibitions' | 'categories' | 'evaluation';
  label: string;
  status: 'pending' | 'active' | 'done' | 'error' | 'skipped';
}

export interface FileError {
  fileIndex: number;
  fileType: string;
  step: SendStep['id'];
  location: string;
  errorMessage: string;
  recoverable: boolean;
  serverErrorDetail?: string;
}

export interface SendProgressState {
  steps: SendStep[];
  currentFile: number;
  totalFiles: number;
  errorMessage?: string;
  fileErrors?: FileError[];
  currentFileProgress?: number;
  isRetryable?: boolean;
  serverErrorDetail?: string;
  warningMessage?: string;
}

export interface GpsCaptureStatus {
  message: string;
}

export interface SendOptions {
  resumeFromStep?: 'mapping' | 'evidences' | 'exhibitions' | 'categories' | 'evaluation';
  timeoutPerFile?: number;
}

export class SendPointCaptureError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SendPointCaptureError';
  }
}

/**
 * Error de dominio para archivos de foto que se referencian pero ya no existen
 * en disco. Permite detener el envío con un mensaje claro y accionable en lugar
 * de abortar con un error genérico.
 */
export class MissingPhotoError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingPhotoError';
  }
}

const GPS_COBERTURA_MESSAGE =
  'No se pudo obtener la ubicación de cierre. Para mejorar la señal GPS: sal a la entrada del local o a la acera (con vista despejada al cielo); aléjate de la bodega, la trastienda y los cuartos fríos; evita pasillos interiores con góndolas altas, techos de lámina o sótanos. Mantén el teléfono en la mano con la pantalla hacia arriba y espera unos segundos antes de reintentar.';

@Injectable({
  providedIn: 'root',
})
export class NewEvaluationService {
  evaluations$: BehaviorSubject<Evaluation[]> = new BehaviorSubject(null);
  sendProgress$ = new Subject<SendProgressState>();
  /** Estado de captura GPS del send_point, consumido por el modal de progreso. */
  gpsStatus$ = new Subject<GpsCaptureStatus>();
  /** Mensaje de advertencia opcional mostrado durante el envío (p. ej. envío largo en celular). */
  private sendWarningMessage?: string;
  /** Tamano minimo en bytes para considerar un blob de imagen valido para upload */
  private static readonly MIN_UPLOAD_IMAGE_SIZE = 128;
  constructor(private storage: StorageService, private http: HttpClient, private fileService: FileService,
    private readonly photoService: PhotoService,
    private readonly authService: AuthService,
    private readonly sentryService: SentryService,
    private readonly networkService: NetworkService,
    private readonly geolocationService: GeolocationService,
  ) {
    this.authService.user$.pipe(
      skipWhile((user) => !user)
    ).subscribe(() => {
      this.reloadEvaluationsForCurrentUser();
    });

    this.registerPersistenceLifecycleHooks();
  }

  /**
   * Fuerza el guardado a storage cuando la app pasa a segundo plano o la página
   * está por cerrarse. Cubre la ventana de tiempo del throttle (300 ms) en la que
   * aún no se ha ejecutado la escritura pendiente.
   */
  private registerPersistenceLifecycleHooks(): void {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        void this.forcePersist();
      }
    });
    window.addEventListener('pagehide', () => {
      void this.forcePersist();
    });
  }

  async init() {
    const legacy = await this.storage.get<Evaluation[]>(StorageEntityName.evaluations2);
    if (legacy !== null) {
      await this.migrateLegacyEvaluations(legacy);
    }
    const evaluations = await this.loadEvaluationsFromStorage();
    this.evaluations$.next(evaluations);
  }

  private reloadEvaluationsForCurrentUser() {
    const allEvaluations: Evaluation[] = this.evaluations$.value || [];
    const currentPromoterId = this.authService.user$.value?.id;
    if (!currentPromoterId) {
      return;
    }
    const migratedEvaluations = allEvaluations.map((e) => {
      if (!e.promoter_id) {
        return { ...e, promoter_id: currentPromoterId };
      }
      return e;
    });
    const myEvaluations = migratedEvaluations.filter(
      (e) => e.promoter_id === currentPromoterId
    );
    this.evaluations$.next(myEvaluations);
  }

  addEvaluation(evaluation: Evaluation): string {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    if (evaluations.some((e) => e.hash === evaluation.hash)) {
      return evaluation.hash;
    }
    evaluation.promoter_id = this.authService.user$.value?.id;
    this.evaluations$.next([...evaluations, evaluation]);
    void this.persistEvaluation(evaluation);
    void this.persistEvaluationIndex([...evaluations, evaluation].map((e) => e.hash));
    return evaluation.hash;
  }

  findEvaluationByHash(hash: string): Evaluation {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    return evaluations.find((evaluation) => evaluation.hash === hash);
  }

  private evaluationKey(hash: string): string {
    return `EVALUATION_${hash}`;
  }

  private async persistEvaluation(evaluation: Evaluation): Promise<void> {
    await this.storage.set(this.evaluationKey(evaluation.hash), evaluation);
  }

  private async persistEvaluationIndex(hashes: string[]): Promise<void> {
    await this.storage.set(StorageEntityName.evaluationsIndex, hashes);
  }

  private async removeEvaluationPersisted(hash: string): Promise<void> {
    await this.storage.remove(this.evaluationKey(hash));
  }

  private async loadEvaluationsFromStorage(): Promise<Evaluation[]> {
    const hashes =
      (await this.storage.get<string[]>(StorageEntityName.evaluationsIndex)) || [];
    const evaluations: Evaluation[] = [];
    for (const hash of hashes) {
      const evaluation = await this.storage.get<Evaluation>(this.evaluationKey(hash));
      if (evaluation) {
        evaluations.push(evaluation);
      }
    }
    return evaluations;
  }

  private async migrateLegacyEvaluations(legacy: Evaluation[]): Promise<void> {
    const hashes: string[] = [];
    for (const evaluation of legacy) {
      await this.storage.set(this.evaluationKey(evaluation.hash), evaluation);
      hashes.push(evaluation.hash);
    }
    await this.storage.set(StorageEntityName.evaluationsIndex, hashes);
    await this.storage.remove(StorageEntityName.evaluations2);
  }

  /**
   * Devuelve la primera evaluación pendiente de envío: cualquier evaluación
   * presente en el arreglo local. Como las evaluaciones enviadas con éxito se
   * eliminan automáticamente, toda evaluación en el arreglo es pendiente.
   */
  getFirstPendingEvaluation(): Evaluation | undefined {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    return evaluations.length > 0 ? evaluations[0] : undefined;
  }

  /**
   * Fuerza la persistencia inmediata de evaluaciones a storage.
   * Se salta el throttleTime(300) — usar antes de un reload forzado,
   * al pasar a segundo plano o al cerrarse la página.
   * No lanza errores: si la persistencia falla, registra un warning.
   */
  async forcePersist(): Promise<void> {
    try {
      const current = this.evaluations$.value;
      if (current !== null) {
        for (const evaluation of current) {
          await this.storage.set(this.evaluationKey(evaluation.hash), evaluation);
        }
        await this.storage.set(
          StorageEntityName.evaluationsIndex,
          current.map((e) => e.hash)
        );
      }
    } catch (error) {
      console.warn('Error al persistir evaluaciones:', error);
    }
  }

  /**
   * Update the evaluation data in the local storage
   * @param hash Hash of the evaluation to be updated
   * @param data Partial data to be updated
   */
  setEvaluationData(hash: string, data: Partial<Evaluation>) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === hash
    );
    if (evaluationIndex === -1) {
      return;
    }
    const updatedEvaluation = {
      ...evaluations[evaluationIndex],
      ...data,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  /**
   * Guarda la URI de una foto de evidencia de categoría (before/after) de forma
   * directa, sin depender del auto-save del formulario. El servicio es singleton,
   * por lo que sobrevive a la destrucción del componente al retroceder.
   */
  saveCategoryPhoto(
    evaluationHash: string,
    categoryPosition: number,
    evidenceIndex: number,
    photoField: 'image_before' | 'image_after',
    uri: string
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    if (!category || !category.photographic_evidences_saved) {
      return;
    }
    const updatedEvidences = [...category.photographic_evidences_saved];
    if (evidenceIndex < 0 || evidenceIndex >= updatedEvidences.length) {
      return;
    }
    updatedEvidences[evidenceIndex] = {
      ...updatedEvidences[evidenceIndex],
      [photoField]: uri,
    };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = {
      ...category,
      photographic_evidences_saved: updatedEvidences,
    };
    const updatedEvaluation = { ...evaluation, categories: updatedCategories };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  /**
   * Persiste atómicamente el collage de una categoría (filas, columnas y rutas
   * locales). Se usa al confirmar el modal para no depender del auto-save
   * debounced del formulario.
   */
  saveCollagePhotos(
    evaluationHash: string,
    categoryPosition: number,
    data: { rows: number; columns: number; filesLocal: string[] }
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    if (!category) {
      return;
    }
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = {
      ...category,
      rows: data.rows,
      columns: data.columns,
      filesLocal: data.filesLocal,
      // Los IDs subidos previamente ya no representan este collage.
      files: [],
      collageFileIds: [],
    };
    const updatedUploadedCategories = (evaluation._uploadedFiles?.categories || []).filter(
      (c) => !(c.categoryIndex === categoryPosition && c.evidenceIndex === -1)
    );
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
      _uploadedFiles: evaluation._uploadedFiles
        ? { ...evaluation._uploadedFiles, categories: updatedUploadedCategories }
        : undefined,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  /**
   * Guarda la URI de una foto individual del collage en la posición indicada
   * (1-based) de `filesLocal`, preservando el resto del arreglo. Se usa en la
   * recuperación post-reinicio de capturas (appRestoredResult).
   */
  saveCollagePhoto(
    evaluationHash: string,
    categoryPosition: number,
    collagePosition: number,
    uri: string
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    if (!category || collagePosition < 1) {
      return;
    }
    const files: string[] = [
      ...(category.filesLocal || (category.files as any as string[]) || []),
    ];
    while (files.length < collagePosition) {
      files.push(undefined as any);
    }
    files[collagePosition - 1] = uri;
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = { ...category, filesLocal: files, files: [], collageFileIds: [] };
    const updatedEvaluation = { ...evaluation, categories: updatedCategories };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  /**
   * Guarda la URI de una evidencia general del instrumento de forma directa.
   */
  setEvidencePhoto(
    evaluationHash: string,
    evidenceIndex: number,
    uri: string
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const updatedEvidencesLocal = [...(evaluation.evidencesLocal || [])];
    if (evidenceIndex < 0) {
      return;
    }
    if (evidenceIndex >= updatedEvidencesLocal.length) {
      updatedEvidencesLocal.push(uri);
    } else {
      updatedEvidencesLocal[evidenceIndex] = uri;
    }
    const updatedEvaluation = {
      ...evaluation,
      evidencesLocal: updatedEvidencesLocal,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  /**
   * Elimina una evidencia general de forma atómica: primero actualiza el estado
   * persistido (sin la URI) y después borra el archivo físico. Si el borrado
   * falla queda un archivo huérfano inofensivo, nunca una referencia colgante.
   */
  async removeEvidencePhoto(evaluationHash: string, evidenceIndex: number): Promise<void> {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }

    const evaluation = evaluations[evaluationIndex];
    const currentEvidences = [...(evaluation.evidencesLocal || [])];
    if (evidenceIndex < 0 || evidenceIndex >= currentEvidences.length) {
      return;
    }
    const removedUri = currentEvidences[evidenceIndex];

    const updatedEvidencesLocal = currentEvidences.filter(
      (_uri, index) => index !== evidenceIndex
    );
    const updatedUploadedEvidences = (
      evaluation._uploadedFiles?.evidences || []
    ).filter((uploaded) => uploaded?.uri !== removedUri);

    const updatedEvaluation: Evaluation = {
      ...evaluation,
      evidencesLocal: updatedEvidencesLocal,
      _uploadedFiles: evaluation._uploadedFiles
        ? { ...evaluation._uploadedFiles, evidences: updatedUploadedEvidences }
        : undefined,
    };

    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);

    // 1º estado persistido (la referencia deja de existir)
    await this.persistEvaluation(updatedEvaluation);

    // 2º archivo físico (huérfano inofensivo si falla)
    if (removedUri) {
      await this.photoService.removeFromPath(removedUri);
    }
  }

  /**
   * Elimina el collage de una categoría de forma atómica: primero quita la
   * referencia del estado persistido y después borra los archivos físicos.
   * Así nunca queda una referencia colgante a un archivo inexistente.
   */
  async removeCollagePhotos(
    evaluationHash: string,
    categoryPosition: number
  ): Promise<void> {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }

    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    if (!category) {
      return;
    }
    const files: string[] = [
      ...(category.filesLocal || (category.files as any as string[]) || []),
    ].filter((path): path is string => typeof path === 'string' && !!path);

    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = {
      ...category,
      rows: undefined,
      columns: undefined,
      filesLocal: [],
      files: [],
      collageFileIds: [],
    };
    const updatedUploadedCategories = (evaluation._uploadedFiles?.categories || []).filter(
      (c) => !(c.categoryIndex === categoryPosition && c.evidenceIndex === -1)
    );
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
      _uploadedFiles: evaluation._uploadedFiles
        ? { ...evaluation._uploadedFiles, categories: updatedUploadedCategories }
        : undefined,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);

    // 1º estado persistido (la referencia deja de existir)
    await this.persistEvaluation(updatedEvaluation);

    // 2º archivos físicos (huérfano inofensivo si falla)
    for (const path of files) {
      try {
        await this.photoService.removeFromPath(path);
      } catch (error) {
        console.error('Error al eliminar foto del collage:', error);
      }
    }
  }

  /**
   * Guarda la URI de la foto de una exhibición adicional de forma directa.
   */
  setExhibitionPhoto(
    evaluationHash: string,
    exhibitionIndex: number,
    uri: string
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const updatedExhibitions = [...(evaluation.exhibitions || [])];
    if (exhibitionIndex < 0 || exhibitionIndex >= updatedExhibitions.length) {
      return;
    }
    updatedExhibitions[exhibitionIndex] = {
      ...updatedExhibitions[exhibitionIndex],
      file: uri,
    };
    const updatedEvaluation = {
      ...evaluation,
      exhibitions: updatedExhibitions,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  /**
   * Elimina la foto de una exhibición de forma atómica: primero quita la
   * referencia del estado persistido y después borra el archivo físico.
   * Así nunca queda una referencia colgante ni un huérfano inofensivo.
   */
  async removeExhibitionPhoto(
    evaluationHash: string,
    exhibitionIndex: number
  ): Promise<void> {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }

    const evaluation = evaluations[evaluationIndex];
    const currentExhibitions = [...(evaluation.exhibitions || [])];
    if (exhibitionIndex < 0 || exhibitionIndex >= currentExhibitions.length) {
      return;
    }
    const removedUri = currentExhibitions[exhibitionIndex]?.file;
    if (!removedUri) {
      return;
    }

    const updatedExhibitions = [...currentExhibitions];
    updatedExhibitions[exhibitionIndex] = {
      ...currentExhibitions[exhibitionIndex],
      file: '',
      file_id: undefined,
    };

    const updatedUploaded = (evaluation._uploadedFiles?.exhibitions || []).filter(
      (u) => !(u && typeof u === 'object' && u.uri === removedUri)
    );

    const updatedEvaluation: Evaluation = {
      ...evaluation,
      exhibitions: updatedExhibitions,
      _uploadedFiles: evaluation._uploadedFiles
        ? { ...evaluation._uploadedFiles, exhibitions: updatedUploaded }
        : undefined,
    };

    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);

    // 1º estado persistido (la referencia deja de existir)
    await this.persistEvaluation(updatedEvaluation);

    // 2º archivo físico (huérfano inofensivo si falla)
    await this.photoService.removeFromPath(removedUri);
  }

  saveCategory(
    evaluationHash: string,
    categoryPosition: number,
    data: Partial<EvaluationCategory>
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const updatedCategory = {
      ...evaluation.categories[categoryPosition],
      ...data,
    };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = updatedCategory;
    const updatedEvaluation = { ...evaluation, categories: updatedCategories };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  saveCategoryRival(
    evaluationHash: string,
    categoryPosition: number,
    rivalPosition: number,
    data: Partial<EvaluationCategoryRival>
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    const updatedRivals = [...category.rivals];
    updatedRivals[rivalPosition] = {
      ...category.rivals[rivalPosition],
      ...data,
    };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = { ...category, rivals: updatedRivals };
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  saveSubCategory(
    evaluationHash: string,
    categoryPosition: number,
    subcategoryPosition: number,
    data: Partial<EvaluationSubCategory>
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    const updatedSubCategories = [...category.sub_categories];
    updatedSubCategories[subcategoryPosition] = {
      ...category.sub_categories[subcategoryPosition],
      ...data,
    };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = {
      ...category,
      sub_categories: updatedSubCategories,
    };
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  saveSubCategoryRival(
    evaluationHash: string,
    categoryPosition: number,
    subcategoryPosition: number,
    subcategoryRivalPosition: number,
    data: Partial<CategorySubcategoryRival>
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === evaluationHash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryPosition];
    const subCategory = category.sub_categories[subcategoryPosition];
    const updatedSubCatRivals = [...subCategory.rivals];
    updatedSubCatRivals[subcategoryRivalPosition] = {
      ...subCategory.rivals[subcategoryRivalPosition],
      ...data,
    };
    const updatedSubCategories = [...category.sub_categories];
    updatedSubCategories[subcategoryPosition] = {
      ...subCategory,
      rivals: updatedSubCatRivals,
    };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryPosition] = {
      ...category,
      sub_categories: updatedSubCategories,
    };
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  saveProduct(
    hash: string,
    index: number,
    categoryIndex: number,
    data: Partial<EvaluationProduct>
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === hash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryIndex];
    const updatedProducts = [...category.products];
    updatedProducts[index] = {
      ...category.products[index],
      ...data,
    };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryIndex] = { ...category, products: updatedProducts };
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  saveProductRival(
    hash: string,
    index: number,
    categoryIndex: number,
    rivalIndex: number,
    data: Partial<EvaluationProductRival>
  ) {
    const evaluations: Evaluation[] = this.evaluations$.value || [];
    const evaluationIndex = evaluations.findIndex(
      (evaluation) => evaluation.hash === hash
    );
    if (evaluationIndex === -1 || !evaluations[evaluationIndex]) {
      return;
    }
    const evaluation = evaluations[evaluationIndex];
    const category = evaluation.categories[categoryIndex];
    const product = category.products[index];
    const updatedProductRivals = [...product.rivals];
    updatedProductRivals[rivalIndex] = {
      ...product.rivals[rivalIndex],
      ...data,
    };
    const updatedProducts = [...category.products];
    updatedProducts[index] = { ...product, rivals: updatedProductRivals };
    const updatedCategories = [...evaluation.categories];
    updatedCategories[categoryIndex] = { ...category, products: updatedProducts };
    const updatedEvaluation = {
      ...evaluation,
      categories: updatedCategories,
    };
    const updatedEvaluations = [...evaluations];
    updatedEvaluations[evaluationIndex] = updatedEvaluation;
    this.evaluations$.next(updatedEvaluations);
    void this.persistEvaluation(updatedEvaluation);
  }

  send(evaluation: Evaluation, options?: SendOptions): Observable<any> {
    const evaluationClone: Evaluation = structuredClone(evaluation);
    const resumeFromStep = options?.resumeFromStep;

    const totalFiles = this.countFilesToUpload(evaluation);
    let uploadedFiles = this.countAlreadyUploadedFiles(evaluation);
    let currentFileName = '';

    this.sendWarningMessage = this.networkService.esCellular() && totalFiles >= 3
      ? 'La conexión es móvil y hay varias fotos por subir. El envío puede tardar varios minutos; mantén la aplicación en primer plano.'
      : undefined;

    const steps: SendStep[] = [
      {
        id: 'mapping', label: 'Preparando datos',
        status: resumeFromStep && resumeFromStep !== 'mapping' ? 'done' : 'pending'
      },
      {
        id: 'evidences', label: 'Subiendo evidencias generales',
        status: resumeFromStep && ['exhibitions', 'categories', 'evaluation'].includes(resumeFromStep) ? 'done'
          : evaluation.evidencesLocal?.length ? 'pending' : 'skipped'
      },
      {
        id: 'exhibitions', label: 'Subiendo fotos de exhibiciones',
        status: resumeFromStep && ['categories', 'evaluation'].includes(resumeFromStep) ? 'done'
          : evaluation.exhibitions?.some(e => e.file) ? 'pending' : 'skipped'
      },
      {
        id: 'categories', label: 'Subiendo fotos de categorías',
        status: resumeFromStep === 'evaluation' ? 'done'
          : this.hasCategoryFiles(evaluation) ? 'pending' : 'skipped'
      },
      {
        id: 'evaluation', label: 'Enviando datos de la evaluación',
        status: 'pending'
      },
    ];

    const firstActive = steps.find(s => s.status === 'pending');
    if (firstActive) firstActive.status = 'active';
    this.emitProgress(steps, uploadedFiles, totalFiles);

    return of(evaluationClone).pipe(
      map((ev) => { validateEvaluation(ev); return ev; }),
      tap(() => {
        const mIdx = steps.findIndex(s => s.id === 'mapping');
        if (mIdx >= 0 && steps[mIdx].status !== 'done') steps[mIdx].status = 'done';
        const next = steps.find(s => s.id === 'evidences');
        if (next && next.status === 'pending') next.status = 'active';
        this.emitProgress(steps, uploadedFiles, totalFiles);
      }),
      switchMap((ev) => from(this.addEvidenceFilesToEvaluation(ev))),
      tap(() => {
        if (steps.find(s => s.id === 'evidences')?.status === 'done') return;
        uploadedFiles += evaluation.evidencesLocal?.length || 0;
        const idx = steps.findIndex(s => s.id === 'evidences');
        if (idx >= 0) steps[idx].status = 'done';
        const next = steps.find(s => s.id === 'exhibitions');
        if (next && next.status === 'pending') next.status = 'active';
        this.emitProgress(steps, uploadedFiles, totalFiles);
        // Persistir el mapeo URI→file_id de evidencias en la evaluación original
        const uploadedEvidences = evaluationClone._uploadedFiles?.evidences || [];
        if (uploadedEvidences.length > 0) {
          this.setEvaluationData(evaluation.hash, {
            _uploadedFiles: {
              evidences: uploadedEvidences,
              exhibitions: evaluation._uploadedFiles?.exhibitions || [],
              categories: evaluation._uploadedFiles?.categories || [],
            }
          });
        }
      }),
      switchMap((evaluationWithEvidences) =>
        from(this.addExhibitionFilesToEvaluation(evaluationWithEvidences))
      ),
      tap(() => {
        if (steps.find(s => s.id === 'exhibitions')?.status === 'done') return;
        uploadedFiles += evaluation.exhibitions?.filter(e => e.file).length || 0;
        const idx = steps.findIndex(s => s.id === 'exhibitions');
        if (idx >= 0) steps[idx].status = 'done';
        const next = steps.find(s => s.id === 'categories');
        if (next && next.status === 'pending') next.status = 'active';
        this.emitProgress(steps, uploadedFiles, totalFiles);
        // Persistir file_ids de exhibiciones en la evaluación original
        const newExhibitionIds: { uri: string; fileId: number }[] = [];
        evaluationClone.exhibitions.forEach((exh) => {
          if (exh.file_id && exh.file && !evaluation._uploadedFiles?.exhibitions?.some(e => e.uri === exh.file)) {
            newExhibitionIds.push({ uri: exh.file, fileId: exh.file_id });
          }
        });
        if (newExhibitionIds.length > 0) {
          this.setEvaluationData(evaluation.hash, {
            _uploadedFiles: {
              // Conservar el mapeo de evidencias recién calculado en el clon
              evidences: evaluationClone._uploadedFiles?.evidences
                || evaluation._uploadedFiles?.evidences
                || [],
              exhibitions: [...(evaluation._uploadedFiles?.exhibitions || []), ...newExhibitionIds],
              categories: evaluation._uploadedFiles?.categories || [],
            }
          });
        }
      }),
      switchMap((evaluationAndFiles) =>
        from(this.addCategoryFilesToEvaluation(evaluationAndFiles))
      ),
      tap(() => {
        if (steps.find(s => s.id === 'categories')?.status === 'done') return;
        uploadedFiles += this.countCategoryFiles(evaluation);
        const idx = steps.findIndex(s => s.id === 'categories');
        if (idx >= 0) steps[idx].status = 'done';
        const next = steps.find(s => s.id === 'evaluation');
        if (next) {
          next.status = 'active';
          next.label = 'Localizando GPS para registrar el punto de cierre…';
        }
        this.emitProgress(steps, totalFiles, totalFiles);
      }),
      switchMap((evaluationWithFiles) =>
        from(this.attachSendPoint(evaluationWithFiles)).pipe(
          tap(() => {
            const evalIdx = steps.findIndex(s => s.id === 'evaluation');
            if (evalIdx >= 0) {
              steps[evalIdx].label = 'Enviando datos de la evaluación';
              this.emitProgress(steps, totalFiles, totalFiles);
            }
          }),
          switchMap((evaluationWithSendPoint) =>
            this.http.post(`evaluations_v2`, toEvaluationPayload(evaluationWithSendPoint)).pipe(
              switchMap((response) =>
                from(this.cleanupEvaluationFiles(evaluation)).pipe(
                  tap(() => {
                    const idx = steps.findIndex(s => s.id === 'evaluation');
                    if (idx >= 0) steps[idx].status = 'done';
                    this.emitProgress(steps, totalFiles, totalFiles);
                    // Limpiar _uploadedFiles y lastSendError tras envío exitoso
                    this.setEvaluationData(evaluation.hash, {
                      _uploadedFiles: undefined,
                      lastSendError: undefined,
                    });
                  }),
                  map(() => response)
                )
              )
            )
          )
        )
      ),
        catchError((error) => {
          const activeIdx = steps.findIndex(s => s.status === 'active');
          if (activeIdx >= 0) steps[activeIdx].status = 'error';
          const statusCode = error?.status || 0;
          const isAuthError = statusCode === 401 || statusCode === 403;
          const isGpsError = error instanceof SendPointCaptureError;
          const isMissingPhoto = error instanceof MissingPhotoError;
          const isValidationError = error instanceof EvaluationValidationError;
          const errorMsg = isGpsError
            ? GPS_COBERTURA_MESSAGE
            : isMissingPhoto
              ? error.message
              : isValidationError
                ? `No se pudo preparar el envío: ${error.message}`
                : this.getFileUploadErrorMessage(error, '', 0);
          const serverDetail = isValidationError
            ? JSON.stringify({ fieldPath: (error as EvaluationValidationError).fieldPath, context: (error as EvaluationValidationError).context }, null, 2)
            : (error as any)?.serverErrorDetail || this.formatServerErrorDetail(error);
          this.emitProgress(
            steps, uploadedFiles, totalFiles, errorMsg,
            undefined, undefined,
            isAuthError ? false : true,
            serverDetail
          );
        // Persistir lastSendError en la evaluación original
        const activeStepObj = steps.find(s => s.status === 'error')
          || steps.find(s => s.status === 'active');
        this.setEvaluationData(evaluation.hash, {
          lastSendError: {
            step: activeStepObj?.id ?? 'unknown',
            message: errorMsg,
            timestamp: new Date().toISOString(),
          }
        });
        return throwError(() => error);
      })
    );
  }

  /**
   * Limpia los archivos locales de una evaluación después de envío exitoso
   * Solo se ejecuta cuando el POST al servidor fue exitoso
   */
  private async cleanupEvaluationFiles(evaluation: Evaluation): Promise<void> {
    const deletePromises: Promise<void>[] = [];

    // Limpiar evidencias generales
    evaluation.evidencesLocal?.forEach((evidence) => {
      const deletePromise = this.photoService.removeFromPath(evidence).catch((err) => {
        console.warn('No se pudo eliminar evidencia:', evidence, err);
        this.sentryService.captureException(err, {
          extra: {
            evidence,
            errorType: 'cleanup_evidence_failed'
          }
        });
      });
      deletePromises.push(deletePromise);
    });

    // Limpiar archivos de exhibiciones
    evaluation.exhibitions?.forEach((exhibition) => {
      if (exhibition.file) {
        const deletePromise = this.photoService.removeFromPath(exhibition.file).catch((err) => {
          console.warn('No se pudo eliminar archivo de exhibición:', exhibition.file, err);
          this.sentryService.captureException(err, {
            extra: {
              exhibitionFile: exhibition.file,
              errorType: 'cleanup_exhibition_failed'
            }
          });
        });
        deletePromises.push(deletePromise);
      }
    });

    // Limpiar archivos de categorías
    evaluation.categories?.forEach((category) => {
      category.photographic_evidences_saved?.forEach((evidence) => {
        if (evidence.image_before) {
          const deletePromise = this.photoService.removeFromPath(evidence.image_before).catch((err) => {
            console.warn('No se pudo eliminar imagen anterior:', evidence.image_before, err);
            this.sentryService.captureException(err, {
              extra: {
                imagePath: evidence.image_before,
                errorType: 'cleanup_category_before_failed'
              }
            });
          });
          deletePromises.push(deletePromise);
        }
        if (evidence.image_after) {
          const deletePromise = this.photoService.removeFromPath(evidence.image_after).catch((err) => {
            console.warn('No se pudo eliminar imagen posterior:', evidence.image_after, err);
            this.sentryService.captureException(err, {
              extra: {
                imagePath: evidence.image_after,
                errorType: 'cleanup_category_after_failed'
              }
            });
          });
          deletePromises.push(deletePromise);
        }
      });

      // Limpiar archivos del collage
      const collageFiles: string[] = category.filesLocal || (category.files as any as string[]) || [];
      collageFiles.forEach((filePath) => {
        const deletePromise = this.photoService.removeFromPath(filePath).catch((err) => {
          console.warn('No se pudo eliminar archivo de collage:', filePath, err);
          this.sentryService.captureException(err, {
            extra: {
              collageFile: filePath,
              errorType: 'cleanup_collage_failed'
            }
          });
        });
        deletePromises.push(deletePromise);
      });
    });

    // Esperar a que todas las eliminaciones terminen
    await Promise.all(deletePromises);
  }


  async addEvidenceFilesToEvaluation(evaluation: Evaluation): Promise<Evaluation> {
    const result = await this.parallelLimit(
      evaluation.evidencesLocal,
      async (evidence, evidenceIndex) => {
        // Red de seguridad: si la evidencia ya se subió en un intento previo
        // (mapeo explícito URI→file_id), reutilizar su file_id sin leer el disco.
        const alreadyUploaded = (evaluation._uploadedFiles?.evidences || []).find(
          (uploaded) =>
            uploaded && typeof uploaded === 'object' && uploaded.uri === evidence
        );
        if (alreadyUploaded) {
          return { uri: evidence, fileId: alreadyUploaded.fileId };
        }

        const evidenceExists = await this.fileService.fileExists(evidence);
        if (!evidenceExists) {
          throw new MissingPhotoError(
            `La evidencia general #${evidenceIndex + 1} no está disponible. Revisa la foto y vuelve a intentar el envío.`
          );
        }

        const blob = await this.photoService.getBlobFromPath(evidence);

        console.log("[addEvidenceFiles] Blob size:", blob.size, "type:", blob.type, "evidence:", evidence);

        if (blob.size < NewEvaluationService.MIN_UPLOAD_IMAGE_SIZE) {
          const errMsg = "No se pudo procesar la evidencia general #" + (evidenceIndex + 1) +
            ": imagen corrupta o demasiado pequena (" + blob.size + " bytes).";
          this.sentryService.captureMessage(errMsg, {
            level: "error",
            extra: { evidence, evidenceIndex, blobSize: blob.size, errorType: "evidence_blob_too_small" }
          });
          throw new Error(errMsg);
        }

        const evidenceFileLoaded = await lastValueFrom(
          this.fileService.uploadFile(blob)
        ).catch((error) => {
          const errorMessage = this.getFileUploadErrorMessage(error, "evidencia general", evidenceIndex + 1);
          this.sentryService.captureException(error, {
            extra: { evidence, evidenceIndex, blobSize: blob.size, connectionType: this.networkService.getConnectionType(), errorType: "evidence_file_upload_failed", statusCode: error?.status || 0 }
          });
          const uploadError = new Error(errorMessage);
          (uploadError as any).serverErrorDetail = this.formatServerErrorDetail(error);
          throw uploadError;
        });

        return { uri: evidence, fileId: evidenceFileLoaded.data.id };
      },
      this.getUploadConcurrencyLimit(3)
    );

    evaluation.evidences.push(...result.map((uploaded) => uploaded.fileId));
    evaluation._uploadedFiles = {
      ...evaluation._uploadedFiles,
      evidences: result,
      exhibitions: evaluation._uploadedFiles?.exhibitions || [],
      categories: evaluation._uploadedFiles?.categories || [],
    };
    return evaluation;
  }


  async addExhibitionFilesToEvaluation(evaluation: Evaluation): Promise<Evaluation> {
    const filesWithIndex = evaluation.exhibitions
      .map((exhibition, i) => ({ exhibition, i }))
      .filter(({ exhibition }) => exhibition.file);

    await this.parallelLimit(filesWithIndex, async ({ exhibition, i }) => {
      // Reutilizar file_id de intentos previos (formato { uri, fileId })
      const alreadyUploaded = (evaluation._uploadedFiles?.exhibitions || []).find(
        (u) => u && typeof u === 'object' && u.uri === exhibition.file
      );
      if (alreadyUploaded) {
        evaluation.exhibitions[i]['file_id'] = alreadyUploaded.fileId;
        return;
      }

      // Verificar si el archivo existe antes de intentar leerlo
      const fileExists = await this.fileService.fileExists(exhibition.file);
      if (!fileExists) {
        const errorMsg = `La foto de la exhibición #${i + 1} no está disponible. Revisa la foto y vuelve a intentar el envío.`;
        this.sentryService.captureMessage(errorMsg, {
          level: 'warning',
          extra: {
            exhibitionFile: exhibition.file,
            exhibitionIndex: i,
            exhibitionId: exhibition.exhibition_id
          }
        });
        throw new MissingPhotoError(errorMsg);
      }

      const blob = await this.photoService.getBlobFromPath(exhibition.file);

      if (blob.size < NewEvaluationService.MIN_UPLOAD_IMAGE_SIZE) {
        const errMsg = `No se pudo procesar la foto de exhibición #${i + 1}: imagen corrupta o demasiado pequeña (${blob.size} bytes).`;
        this.sentryService.captureMessage(errMsg, {
          level: 'error',
          extra: {
            exhibitionFile: exhibition.file,
            exhibitionIndex: i,
            blobSize: blob.size,
            errorType: 'exhibition_blob_too_small'
          }
        });
        throw new Error(errMsg);
      }

      const evidenceFileLoaded = await lastValueFrom(
        this.fileService.uploadFile(blob)
      ).catch((error) => {
        const errorMessage = this.getFileUploadErrorMessage(error, 'exhibición', i + 1);
        this.sentryService.captureException(error, {
          extra: {
            exhibitionFile: exhibition.file,
            exhibitionIndex: i,
            connectionType: this.networkService.getConnectionType(),
            errorType: 'exhibition_file_upload_failed',
            statusCode: error?.status || 0,
          }
        });
        const uploadError = new Error(errorMessage);
        (uploadError as any).serverErrorDetail = this.formatServerErrorDetail(error);
        throw uploadError;
      });

      evaluation.exhibitions[i]['file_id'] = evidenceFileLoaded.data.id;
    }, this.getUploadConcurrencyLimit(3));

    return evaluation;
  }

  private async addCollageFilesToCategory(
    category: EvaluationCategory,
    categoryIndex: number,
    persistedCollageIds: number[] = []
  ): Promise<{ category: EvaluationCategory; collageFileIds: number[] }> {
    const collageFiles: string[] = category.filesLocal || (category.files as any as string[]) || [];

    if (collageFiles.length === 0) {
      return { category, collageFileIds: [] };
    }

    const uploadedFileIds: number[] = new Array(collageFiles.length);

    await this.parallelLimit(collageFiles, async (filePath, i) => {
      if (persistedCollageIds[i] !== undefined) {
        uploadedFileIds[i] = persistedCollageIds[i];
        return;
      }

      // Hueco en filesLocal (posición sin foto del recovery): se omite sin subir.
      if (!filePath) {
        return;
      }

      const fileExists = await this.fileService.fileExists(filePath);
      if (!fileExists) {
        throw new MissingPhotoError(
          `La foto de collage #${i + 1} de la categoría #${categoryIndex + 1} no está disponible. Revisa la foto y vuelve a intentar el envío.`
        );
      }

      const blob = await this.photoService.getBlobFromPath(filePath);

      if (blob.size < NewEvaluationService.MIN_UPLOAD_IMAGE_SIZE) {
        const errMsg = `No se pudo procesar la foto de collage #${i + 1}: imagen corrupta o demasiado pequeña (${blob.size} bytes).`;
        this.sentryService.captureMessage(errMsg, {
          level: 'error',
          extra: { filePath, collageIndex: i, blobSize: blob.size, errorType: 'collage_blob_too_small' }
        });
        throw new Error(errMsg);
      }

        const uploadResult = await lastValueFrom(this.fileService.uploadFile(blob)).catch((error) => {
          const errorMessage = this.getFileUploadErrorMessage(error, 'foto de collage', i + 1);
          this.sentryService.captureException(error, {
            extra: {
              filePath,
              collageIndex: i,
              connectionType: this.networkService.getConnectionType(),
              errorType: 'collage_file_upload_failed',
              statusCode: error?.status || 0
            }
          });
          const uploadError = new Error(errorMessage);
          (uploadError as any).serverErrorDetail = this.formatServerErrorDetail(error);
          throw uploadError;
        });

      uploadedFileIds[i] = uploadResult.data.id;
    }, this.getUploadConcurrencyLimit(3));

    return {
      category: {
        ...category,
        files: uploadedFileIds.filter(id => id !== undefined) as any as number[],
        collageFileIds: uploadedFileIds,
      },
      collageFileIds: uploadedFileIds,
    };
  }

  /**
   * Sube una imagen individual de categoria (before/after) y retorna el ID del archivo.
   * Centraliza la logica duplicada que existia para image_before e image_after.
   * Persiste el ID subido en _uploadedFiles.categories para reintentos parciales.
   */
  private async uploadCategoryImage(
    imagePath: string,
    evidence: any,
    evidenceIndex: number,
    imageType: 'anterior' | 'posterior',
    categoryIndex: number,
    hash: string
  ): Promise<number> {
    const imageExists = await this.fileService.fileExists(imagePath);
    if (!imageExists) {
      throw new MissingPhotoError(
        `La imagen ${imageType} de la categoría #${categoryIndex + 1} no está disponible. Revisa la foto y vuelve a intentar el envío.`
      );
    }

    const blob = await this.photoService.getBlobFromPath(imagePath);

    console.log(
      `[addCategoryFiles:${imageType}] Blob size:`,
      blob.size,
      'type:',
      blob.type,
      'path:',
      imagePath
    );

    if (blob.size < NewEvaluationService.MIN_UPLOAD_IMAGE_SIZE) {
      const errMsg =
        `No se pudo procesar la imagen ${imageType} de categoria #${evidenceIndex + 1}: ` +
        `imagen corrupta o demasiado pequena (${blob.size} bytes).`;
      this.sentryService.captureMessage(errMsg, {
        level: 'error',
        extra: {
          evidence,
          evidenceIndex,
          categoryIndex,
          blobSize: blob.size,
          errorType: `category_${imageType === 'anterior' ? 'before' : 'after'}_blob_too_small`,
        },
      });
      throw new Error(errMsg);
    }

    const uploadResult = await lastValueFrom(this.fileService.uploadFile(blob)).catch((error) => {
      this.sentryService.captureException(error, {
        extra: {
          imagePath,
          imageType,
          evidenceIndex,
          categoryIndex,
          blobSize: blob.size,
          connectionType: this.networkService.getConnectionType(),
          errorType: 'category_file_upload_failed',
          statusCode: error?.status || 0,
        },
      });
      throw error;
    });
    const fileId = uploadResult.data.id;

    // Persistir progreso parcial en _uploadedFiles
    const uploaded = this.findEvaluationByHash(hash)?._uploadedFiles;
    const catEntry = uploaded?.categories?.find(
      (c) => c.categoryIndex === categoryIndex && c.evidenceIndex === evidenceIndex
    ) ?? { categoryIndex, evidenceIndex };

    if (imageType === 'anterior') {
        catEntry.imageBeforeId = fileId;
    } else {
      catEntry.imageAfterId = fileId;
    }

    const existingCategories = uploaded?.categories?.filter(
      (c) => !(c.categoryIndex === categoryIndex && c.evidenceIndex === evidenceIndex)
    ) ?? [];

    this.setEvaluationData(hash, {
      _uploadedFiles: {
        evidences: uploaded?.evidences ?? [],
        exhibitions: uploaded?.exhibitions ?? [],
        categories: [...existingCategories, catEntry],
      },
    });

    return fileId;
  }

  async addCategoryFilesToEvaluation(evaluation: Evaluation): Promise<Evaluation> {
    const processedCategories = await this.parallelLimit(
      evaluation.categories,
      async (category, catIndex) => {
        const evidenceResults = await this.parallelLimit(
          category.photographic_evidences_saved,
          async (evidence, evidenceIndex) => {
            const uploaded = evaluation._uploadedFiles?.categories?.find(
              (c) =>
                c.categoryIndex === catIndex &&
                c.evidenceIndex === evidenceIndex
            );

            const response = {
              image_before: (uploaded?.imageBeforeId as number) ?? null,
              image_after: (uploaded?.imageAfterId as number) ?? null,
              image_type_id: evidence.image_type_id,
              sku: evidence.sku,
              products: evidence.products ?? [],
              cenefa_sticker: evidence.cenefa_sticker,
              price_tag: evidence.price_tag,
            };

            if (evidence.image_before && !uploaded?.imageBeforeId) {
              response.image_before = await this.uploadCategoryImage(
                evidence.image_before, evidence, evidenceIndex, 'anterior', catIndex, evaluation.hash
              );
            }

            if (evidence.image_after && !uploaded?.imageAfterId) {
              response.image_after = await this.uploadCategoryImage(
                evidence.image_after, evidence, evidenceIndex, 'posterior', catIndex, evaluation.hash
              );
            }

            return response;
          },
          this.getUploadConcurrencyLimit(2)
        );

        const categoryWithEvidences = {
          ...category,
          photographic_evidences: evidenceResults,
        };

        const freshUploaded = this.findEvaluationByHash(evaluation.hash)?._uploadedFiles;
        const persistedCollage = (freshUploaded?.categories || []).find(
          (c) => c.categoryIndex === catIndex && c.evidenceIndex === -1
        );
        const { category: categoryWithCollage, collageFileIds } =
          await this.addCollageFilesToCategory(
            categoryWithEvidences,
            catIndex,
            persistedCollage?.collageFileIds ?? []
          );

        if (collageFileIds.length > 0) {
          const latestUploaded = this.findEvaluationByHash(evaluation.hash)?._uploadedFiles;
          const existingCats = (latestUploaded?.categories || []).filter(
            (c) => !(c.categoryIndex === catIndex && c.evidenceIndex === -1)
          );
          this.setEvaluationData(evaluation.hash, {
            _uploadedFiles: {
              evidences: latestUploaded?.evidences ?? [],
              exhibitions: latestUploaded?.exhibitions ?? [],
              categories: [
                ...existingCats,
                { categoryIndex: catIndex, evidenceIndex: -1, collageFileIds },
              ],
            },
          });
        }

        return categoryWithCollage;
      },
      this.getUploadConcurrencyLimit(2)
    );

    return { ...evaluation, categories: processedCategories };
  }

  async deleteFromLocal(hash: string) {
    const evaluations: Evaluation[] = [...this.evaluations$.value];
    const newEvaluations = evaluations.filter(
      (evaluation) => evaluation.hash !== hash
    );
    const evaluationForDeletion = evaluations.find(
      (evaluationSaved) => evaluationSaved.hash === hash
    );

    // 1º quitar la referencia persistida (evita dejar una evaluación apuntando
    // a archivos que se van a borrar)
    this.evaluations$.next(newEvaluations);
    await this.removeEvaluationPersisted(hash);
    await this.persistEvaluationIndex(newEvaluations.map((e) => e.hash));

    // 2º borrar los archivos físicos (un huérfano es inofensivo)
    if (evaluationForDeletion) {
      await this.deleteCategoryPhotos(evaluationForDeletion);
      await this.deleteEvidencesPhotos(evaluationForDeletion);
      await this.deleteExhibitionsPhotos(evaluationForDeletion);
    }
  }

  /** Límite de concurrencia de subida según tipo de conexión (S4). */
  private getUploadConcurrencyLimit(defaultLimit: number): number {
    return this.networkService.esCellular() ? 1 : defaultLimit;
  }

  /**
   * Ejecuta una función asíncrona sobre un array de items con un límite
   * máximo de concurrencia. Útil para no saturar la red con demasiadas
   * subidas simultáneas.
   */
  private async parallelLimit<T, R>(
    items: T[],
    fn: (item: T, index: number) => Promise<R>,
    limit: number = 3
  ): Promise<R[]> {
    const results: R[] = new Array(items.length);
    const inFlight = new Set<Promise<void>>();

    for (let i = 0; i < items.length; i++) {
      const p = fn(items[i], i).then((r) => {
        results[i] = r;
      });
      const tracker = p.then(() => { inFlight.delete(tracker); });
      inFlight.add(tracker);

      if (inFlight.size >= limit) {
        await Promise.race([...inFlight]);
      }
    }

    await Promise.all([...inFlight]);
    return results;
  }

  /** Cuenta el número total de archivos a subir para la barra de progreso */
  private countFilesToUpload(evaluation: Evaluation): number {
    let count = evaluation.evidencesLocal?.length || 0;
    count += evaluation.exhibitions?.filter(e => e.file).length || 0;
    count += this.countCategoryFiles(evaluation);
    return count;
  }

  private countCategoryFiles(evaluation: Evaluation): number {
    let count = 0;
    evaluation.categories?.forEach(category => {
      category.photographic_evidences_saved?.forEach(evidence => {
        if (evidence.image_before) count++;
        if (evidence.image_after) count++;
      });
      const collageFiles: string[] = category.filesLocal || (category.files as any as string[]) || [];
      count += collageFiles.length;
    });
    return count;
  }

  private hasCategoryFiles(evaluation: Evaluation): boolean {
    return this.countCategoryFiles(evaluation) > 0;
  }

  private countAlreadyUploadedFiles(evaluation: Evaluation): number {
    let count = 0;
    if (evaluation._uploadedFiles) {
      count += evaluation._uploadedFiles.evidences.length;
      count += evaluation._uploadedFiles.exhibitions.length;
      evaluation._uploadedFiles.categories.forEach(cat => {
        if (cat.imageBeforeId) count++;
        if (cat.imageAfterId) count++;
        if (cat.collageFileIds) count += cat.collageFileIds.length;
      });
    }
    return count;
  }

  private emitProgress(
    steps: SendStep[],
    currentFile: number,
    totalFiles: number,
    errorMessage?: string,
    fileErrors?: FileError[],
    currentFileProgress?: number,
    isRetryable?: boolean,
    serverErrorDetail?: string
  ): void {
    this.sendProgress$.next({
      steps: [...steps],
      currentFile,
      totalFiles,
      errorMessage,
      fileErrors,
      currentFileProgress,
        isRetryable,
        serverErrorDetail,
        warningMessage: this.sendWarningMessage,
      });
    }

  checkIfEvaluationHaveGeoLocation(evaluation: Evaluation): boolean {
    return evaluation.latitude !== null && evaluation.longitude !== null;
  }

  /**
   * Captura la posición de envío (send_point) justo antes del POST.
   * Es bloqueante: si no se obtiene la ubicación, lanza un error que aborta el envío.
   */
  private async attachSendPoint(evaluation: Evaluation): Promise<Evaluation> {
    try {
      const position = await this.geolocationService.getSendPointPosition((attempt, maxAttempts) => {
        this.gpsStatus$.next({
          message:
            attempt === 1
              ? `Buscando señal GPS… (intento ${attempt} de ${maxAttempts})`
              : `Señal GPS débil. Reintentando… (intento ${attempt} de ${maxAttempts})`,
        });
      });
      return {
        ...evaluation,
        send_point: {
          lat: position.coords.latitude,
          long: position.coords.longitude,
        },
      };
    } catch (e) {
      if (!this.geolocationService.isTimeoutError(e)) {
        this.sentryService.captureException(e, { extra: { errorType: 'send_point_capture_failed' } });
      }
      throw new SendPointCaptureError('No se pudo obtener la ubicación de envío (send_point).');
    }
  }


  private async deleteCategoryPhotos(evaluation: Evaluation): Promise<void> {
    for (const category of evaluation.categories) {
      for (const evidence of (category.photographic_evidences_saved || [])) {
        if (evidence.image_before) {
          await this.photoService.removeFromPath(evidence.image_before).catch(() => { });
        }
        if (evidence.image_after) {
          await this.photoService.removeFromPath(evidence.image_after).catch(() => { });
        }
      }

      // Eliminar archivos del collage
      const collageFiles: string[] = category.filesLocal || (category.files as any as string[]) || [];
      for (const filePath of collageFiles) {
        await this.photoService.removeFromPath(filePath).catch(() => { });
      }
    }
  }

  private async deleteEvidencesPhotos(evaluation: Evaluation): Promise<void> {
    for (const evidence of (evaluation.evidencesLocal || [])) {
      await this.photoService.removeFromPath(evidence).catch(() => { });
    }
  }

  private async deleteExhibitionsPhotos(evaluation: Evaluation): Promise<void> {
    for (const exhibition of (evaluation.exhibitions || [])) {
      if (exhibition.file) {
        await this.photoService.removeFromPath(exhibition.file).catch(() => { });
      }
    }
  }

  // async getEvidenceByClientId(clientId: number): Promise<any> {
  //   const evicencesTypeStoraged = await this.storage.get<EvidenceType[]>(StorageEntityName.evidenceTypes);
  //   return evicencesTypeStoraged.find((e) => e.client_id === clientId);

  // }
  getAllRemote(params?: {
    page?: any;
    search?: any;
    orderBy?: any;
    sortedBy?: any;
    per_page?: any;
  }) {
    return this.http.get<PaginatedEvaluationsRemotes>(`evaluations`, { params });
  }


  async getBrandsByCustomerId(customerId: number) {
    const promoter = await this.storage.get<any>(StorageEntityName.promoter);

    const customer = promoter.customers.find((brand: { id: number; }) => brand.id === customerId);
    return customer?.brands;
  }

  async getEventTypesByCustomerId(customerId: number) {
    const promoter = await this.storage.get<any>(StorageEntityName.promoter);
    const customer = promoter.customers.find((event_type: { id: number; }) => event_type.id === customerId);
    return customer?.event_types;
  }

  private formatServerErrorDetail(error: any): string | undefined {
    if (error?.error && typeof error.error === 'object' && Object.keys(error.error).length > 0) {
      try {
        return JSON.stringify(error.error, null, 2);
      } catch {
        return String(error.error);
      }
    }
    return undefined;
  }

  /**
   * Genera un mensaje de error específico basado en el código HTTP y el tipo de archivo
   * @param error Error HTTP recibido
   * @param fileType Tipo de archivo (ej: 'evidencia general', 'exhibición')
   * @param fileNumber Número del archivo en la secuencia
   * @returns Mensaje de error descriptivo para el usuario
   */
  private getFileUploadErrorMessage(error: any, fileType: string, fileNumber: number): string {
    const statusCode = error?.status || 0;
      const baseMessage = `Error al subir la ${fileType} #${fileNumber}`;

      if (error instanceof TimeoutError || error?.name === 'TimeoutError') {
        return `Conexión demasiado lenta para subir la ${fileType} #${fileNumber}. Acércate a una mejor señal e inténtalo de nuevo.`;
      } else if (statusCode === 0) {
        return `${baseMessage}: Sin conexión a internet. Verifica tu conexión e intenta nuevamente.`;
    } else if (statusCode === 413) {
      return `${baseMessage}: El archivo es demasiado grande. Intenta con una imagen de menor tamaño.`;
    } else if (statusCode === 415) {
      return `${baseMessage}: Formato de archivo no válido. Usa imágenes en formato JPG o PNG.`;
    } else if (statusCode === 401 || statusCode === 403) {
      return `${baseMessage}: Tu sesión ha expirado. Por favor, inicia sesión nuevamente.`;
    } else if (statusCode === 422) {
      return `${baseMessage}: El archivo está corrupto o es inválido.`;
    } else if (statusCode >= 500) {
      return `${baseMessage}: Error en el servidor. Intenta más tarde.`;
    }

    return `${baseMessage}: Error desconocido. Intenta nuevamente.`;
  }

  async syncPendingEvaluations(
    onProgress?: (current: number, total: number) => void
  ): Promise<{ sent: number; failed: number }> {
    const allEvaluations: Evaluation[] = this.evaluations$.value || [];
    const pendingToSend = allEvaluations.filter(
      (e) => e.finished_at && e.latitude != null && e.longitude != null
    );

    if (pendingToSend.length === 0) {
      return { sent: 0, failed: 0 };
    }

    let sent = 0;
    let failed = 0;

    for (let i = 0; i < pendingToSend.length; i++) {
      if (onProgress) {
        onProgress(i + 1, pendingToSend.length);
      }

      try {
        await lastValueFrom(this.send(pendingToSend[i]));
        await this.deleteFromLocal(pendingToSend[i].hash);
        sent++;
      } catch (error) {
        const statusCode = error?.status || 0;
        failed++;

        if (statusCode === 401 || statusCode === 403) {
          const remaining = pendingToSend.slice(i + 1);
          failed += remaining.length;
          break;
        }
      }
    }

    return { sent, failed };
  }

}
